/**
 * FlightVoucherBody — Travcations Flight Voucher renderer.
 *
 * Supports TWO schemas (backward-compatible):
 *
 *   1. LEGACY FLAT  → voucher.flight = { airline, flightNumber, pnr,
 *      departureCity, departureAirport, departureDate, departureTime,
 *      arrivalCity, arrivalAirport, arrivalDate, arrivalTime, travelClass,
 *      baggage, seatNumber, passengerNames[]? }
 *      Rendered as a single-segment compact layout (mirrors the pre-rebuild
 *      output 1:1 for the 10 legacy vouchers in production).
 *
 *   2. RICH MULTI-SEGMENT / MULTI-PAX → voucher.flight = { tripType,
 *      bookingReference, eTicketNumber, segments[], passengers[].perSegment[]
 *      …plus legacy fields mirrored from first segment + lead passenger }.
 *
 * Edit affordances (mirror Hotel pattern):
 *   - editable=true  → pencil icons on Name / Seat / Special Meal / Special
 *     Assistance / Baggage / Meals / Special Requests.
 *   - locked=true    → Lock icons replace pencils (status=ISSUED).
 *   - onFieldSave(fieldKey, value) → PATCH /api/vouchers/{id}; supports
 *     dotted keys like `flight.passengers.0.fullName` for index-addressed
 *     updates.
 *
 * Per-passenger PDF:
 *   - passengerIndex = N (integer) → render only passengers[N] (single card)
 *     but keep ALL segments visible.
 *   - passengerIndex = null → render up to first 9 passengers in one PDF.
 *
 * Group cap:
 *   - Default group view is capped at 9 passengers per file. When the
 *     passengers[] array exceeds 9, a warning banner is rendered and the
 *     agent must download additional passengers individually.
 *
 * Barcode → Passenger mapping (Option B, deterministic ordered):
 *   code index = passengerIdx * segments.length + segmentIdx
 *   No code at that index → render nothing.
 *
 * PDF safety: no `display: flex` anywhere. CSS-table / inline-block / grid
 * only (same rule as VoucherPrimitives.js).
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { User, Heart, AlertTriangle, Trash2, Upload, RefreshCw, FileText, X } from 'lucide-react';
import { SectionTitle, InfoCard } from './BrandedVoucherLayout';
import { SectionCard, has } from './VoucherPrimitives';
import { InlineEditableField } from './VoucherEditComponents';
import VoucherBarcodeStrip from './VoucherBarcodeStrip';
import FlightIcon from './FlightIcon';
import { formatVoucherDate } from '../../lib/voucherDate';
import { deriveTripType, buildHeaderRoute, formatAirportName } from '../../lib/flightRoute';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TRIP_TYPE_LABEL = {
  'one-way': 'One-way',
  'round-trip': 'Round-trip',
  'multi-city': 'Multi-city',
};

const PASSENGERS_PER_PDF_CAP = 9;

// IATA pax-type codes → human labels. The vision LLM is prompted to return
// the human form already, but some legacy/cached vouchers carry the raw
// IATA code (ADT / CHD / INF) — normalise at render time too.
const PAX_TYPE_LABELS = {
  ADT: 'Adult', ADULT: 'Adult', AD: 'Adult', ADULTS: 'Adult',
  CHD: 'Child', CHILD: 'Child', CH: 'Child',
  INF: 'Infant', INFANT: 'Infant', IN: 'Infant',
};
const normalisePaxType = (raw) => {
  if (!raw) return '';
  const k = String(raw).trim().toUpperCase();
  return PAX_TYPE_LABELS[k] || raw;
};

// IATA SSR + meal codes → full human form. Used by the per-segment SSR chips
// so agents and customers can read 'WCHR · Wheelchair – Ramp' instead of
// just 'WCHR'. Extend freely — anything not in the table just renders the
// raw code with no label suffix.
const SSR_LABELS = {
  // Meal codes
  AVML: 'Asian Vegetarian Meal',
  VGML: 'Vegetarian Meal',
  KSML: 'Kosher Meal',
  BBML: 'Baby Meal',
  CHML: 'Child Meal',
  DBML: 'Diabetic Meal',
  FPML: 'Fruit Platter Meal',
  GFML: 'Gluten Free Meal',
  HNML: 'Hindu Meal',
  LSML: 'Low Salt Meal',
  MOML: 'Muslim Meal',
  NLML: 'Low Lactose Meal',
  ORML: 'Oriental Meal',
  RVML: 'Raw Vegetarian Meal',
  SFML: 'Seafood Meal',
  VJML: 'Vegetarian Jain Meal',
  VLML: 'Vegetarian Lacto-Ovo Meal',
  // Assistance
  WCHR: 'Wheelchair – Ramp',
  WCHC: 'Wheelchair – Cabin Seat',
  WCHS: 'Wheelchair – Steps',
  BLND: 'Blind Passenger',
  DEAF: 'Deaf Passenger',
  MEDA: 'Medical Assistance',
  OXYG: 'Oxygen Required',
  STCR: 'Stretcher Required',
  UMNR: 'Unaccompanied Minor',
  EXST: 'Extra Seat',
  CBBG: 'Cabin Baggage',
  SPEQ: 'Sports Equipment',
  SRCT: 'Security Restricted',
};

// Format an SSR code as "CODE · Full Form" when the code is in the lookup,
// else just return the code (or the original verbatim string if it wasn't
// a known IATA code at all — e.g. an airline-specific code like "VLML/B").
const formatSsrCode = (raw) => {
  if (raw === null || raw === undefined) return '';
  const s = String(raw).trim();
  if (!s) return '';
  const upper = s.toUpperCase();
  const label = SSR_LABELS[upper];
  return label ? `${upper} · ${label}` : s;
};

// Currency-safe numeric parser. The vision LLM extracts fare amounts as
// strings WITH currency symbols and grouping separators (e.g. "₹7,581.00",
// "USD 1,234", "AED 9 620"). Naive parseFloat returns NaN for any input
// starting with a non-digit character (₹, $, currency code) — wiping the
// whole tax breakdown to 0. Strip every non-numeric character except the
// decimal point and minus sign before parsing.
const toNum = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const cleaned = String(v).replace(/[^\d.\-]/g, '');
  if (!cleaned || cleaned === '.' || cleaned === '-') return 0;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

// Barcode → passenger mapping.
//
// Two operating modes:
//
//   1. SINGLE-CODE BROADCAST — e-tickets carry ONE PNR-encoded barcode that
//      validates every coupon on the booking. When we only detected one code
//      we hand the same code to every (passenger × segment) cell so all
//      sectors render with the same scannable strip.
//
//   2. OPTION B INDEXED (boarding-pass style) — when the supplier PDF holds
//      multiple barcodes (typically one per segment per passenger, e.g. a
//      proper boarding-pass packet), assign them deterministically using:
//        code index = passengerIdx * segmentsLen + segmentIdx
//      with codes sorted (page, top, left) server-side. Missing index → null.
function getPassengerCode(passengerIdx, segmentIdx, segmentsLen, allCodes) {
  if (!Array.isArray(allCodes) || allCodes.length === 0) return null;
  // 1) MANUAL overrides win. Agent uploaded a hand-cropped barcode for this
  //    exact (pIdx, sIdx) — render that, no positional fallback.
  const manual = allCodes.find(
    (c) => c
      && c.format === 'MANUAL'
      && c.passengerIdx === passengerIdx
      && c.segmentIdx === segmentIdx,
  );
  if (manual) return manual;
  // 2) Auto-detected codes are positional. Strip MANUAL entries from the
  //    pool first so they don't displace the auto-mapping for sectors that
  //    DON'T have a manual override yet.
  const auto = allCodes.filter((c) => c && c.format !== 'MANUAL');
  if (auto.length === 0) return null;
  if (auto.length === 1) return auto[0];
  const idx = (passengerIdx * Math.max(segmentsLen, 1)) + segmentIdx;
  return auto[idx] || null;
}

function fmtDateTime(date, time) {
  return [formatVoucherDate(date), time].filter(Boolean).join(' · ');
}

// SsrChip — small pill rendered next to seat number. Meal codes get a
// brand-tinted chip; everything else (WCHR, WCHC, BLND, MEDA, …) gets the
// neutral light-gray chip. Empty / null values render nothing.
// IATA meal vs assistance partition — drives palette and the seed-button
// dropdown's preset list.
const MEAL_CODES = new Set([
  'AVML', 'VGML', 'KSML', 'BBML', 'CHML', 'DBML', 'FPML', 'GFML',
  'HNML', 'LSML', 'MOML', 'NLML', 'ORML', 'RVML', 'SFML', 'VJML', 'VLML',
]);
const SSR_ASSISTANCE_CODES = new Set([
  'WCHR', 'WCHC', 'WCHS', 'BLND', 'DEAF', 'MEDA', 'OXYG',
  'STCR', 'UMNR', 'EXST', 'CBBG', 'SPEQ', 'SRCT',
]);

// Inline "+ Meal" / "+ SSR" seed button. Renders as a small ghost-style
// chip that opens a single-level dropdown of the IATA preset codes (sourced
// from MEAL_CODES / SSR_ASSISTANCE_CODES) plus a "Custom…" sentinel that
// asks for a free-text code. Selection persists via the supplied onSave —
// the seat-row caller wires this to onFieldSave(...perSegment.{j}.specialMeal).
function SsrSeedButton({ kind, primary, accent, onSave, testId }) {
  const [open, setOpen] = useState(false);
  const codes = kind === 'meal'
    ? Array.from(MEAL_CODES)
    : Array.from(SSR_ASSISTANCE_CODES);
  const label = kind === 'meal' ? '+ Meal' : '+ SSR';
  const promptLabel = kind === 'meal' ? 'meal code' : 'SSR code';

  const handlePick = (code) => {
    setOpen(false);
    if (code === '__custom__') {
      // eslint-disable-next-line no-alert
      const raw = window.prompt(`Enter custom IATA ${promptLabel} (e.g. ${kind === 'meal' ? 'AVML' : 'WCHR'}):`);
      const v = (raw || '').trim().toUpperCase();
      if (v) onSave?.(v);
      return;
    }
    onSave?.(code);
  };

  return (
    <span style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle' }}>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        style={{
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.06em',
          border: `1px dashed ${accent}`,
          background: 'transparent',
          color: accent,
          cursor: 'pointer',
          lineHeight: 1.4,
        }}
      >
        {label}
      </button>
      {open && (
        <div
          data-testid={`${testId}-menu`}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 60,
            minWidth: '220px',
            maxHeight: '260px',
            overflowY: 'auto',
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '6px',
            boxShadow: '0 6px 24px rgba(15, 23, 42, 0.14)',
            padding: '4px 0',
          }}
        >
          {codes.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => handlePick(code)}
              data-testid={`${testId}-opt-${code}`}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '6px 12px',
                fontSize: '11px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#1F2937',
                lineHeight: 1.3,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${primary}10`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontFamily: 'monospace', fontWeight: 700, marginRight: '8px' }}>{code}</span>
              <span style={{ color: accent }}>{SSR_LABELS[code] || ''}</span>
            </button>
          ))}
          <div style={{ borderTop: '1px solid #F3F4F6', margin: '4px 0' }} />
          <button
            type="button"
            onClick={() => handlePick('__custom__')}
            data-testid={`${testId}-opt-custom`}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '6px 12px',
              fontSize: '11px',
              fontStyle: 'italic',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: accent,
            }}
          >
            Custom…
          </button>
        </div>
      )}
    </span>
  );
}

function SsrChip({ code, primary, accent }) {
  if (!code) return null;
  const c = String(code).trim();
  if (!c) return null;
  const upper = c.toUpperCase();
  const label = SSR_LABELS[upper];
  const isMeal = MEAL_CODES.has(upper);
  const palette = isMeal
    ? { background: `${primary}12`, color: primary, border: 'none' }
    : { background: '#F8FAFC', color: accent, border: '1px solid #E5E7EB' };
  return (
    <span
      data-testid={`ssr-chip-${upper}`}
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        marginLeft: '6px',
        borderRadius: '4px',
        fontSize: '10px',
        letterSpacing: '0.06em',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
        fontFamily: 'inherit',
        ...palette,
      }}
    >
      <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{upper}</span>
      {label && (
        <>
          <span aria-hidden="true" style={{ margin: '0 5px', color: accent, opacity: 0.5 }}>·</span>
          <span style={{ fontWeight: 400 }}>{label}</span>
        </>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// EditableInfoCard — InfoCard-shaped card whose value is inline-editable.
// ---------------------------------------------------------------------------
function EditableInfoCard({
  label, value, mono = false,
  fieldKey, editable, locked, onFieldSave,
  primary, accent,
}) {
  if (!editable && !locked) {
    return <InfoCard label={label} value={value} primary={primary} accent={accent} mono={mono} />;
  }
  return (
    <div
      style={{
        padding: '14px 16px',
        background: '#F8FAFC',
        borderRadius: '10px',
        border: '1px solid #E5E7EB',
      }}
      data-testid={`flight-editable-${fieldKey}-card`}
    >
      <div
        className="text-[9px] font-semibold uppercase mb-1.5"
        style={{ letterSpacing: '0.18em', color: accent || '#595A5B' }}
      >
        {label}
      </div>
      <InlineEditableField
        value={value || ''}
        variant="text"
        editable={editable}
        locked={locked}
        onSave={(v) => onFieldSave?.(fieldKey, v)}
        textareaRows={1}
        ariaLabel={`Edit ${label}`}
        testIdPrefix={`flight-${fieldKey}`}
        displayClassName={mono ? 'font-mono text-sm font-bold' : 'text-sm font-bold'}
        displayStyle={{ color: '#111827', wordBreak: 'break-word' }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// LegacyFlightLayout — preserves the pre-rebuild output for the 10 existing
// flat-schema vouchers. Adds inline pencils on Name / Seat / Meals /
// Special Requests / Baggage when editable.
// ---------------------------------------------------------------------------
function LegacyFlightLayout({ voucher, primary, accent, editable, locked, onFieldSave }) {
  const f = voucher?.flight || {};
  const fromCode = f.from || f.departureAirport || '';
  const toCode = f.to || f.arrivalAirport || '';
  const fromCity = f.fromCity || f.departureCity || '';
  const toCity = f.toCity || f.arrivalCity || '';
  const cabinClass = f.cabinClass || f.travelClass || 'Economy';
  const departureDT = f.departureDateTime || fmtDateTime(f.departureDate, f.departureTime);
  const arrivalDT = f.arrivalDateTime || fmtDateTime(f.arrivalDate, f.arrivalTime);

  const orig = voucher?.originalCode;
  const codeKind = orig?.kind || 'barcode';
  const barcodeValue = (orig?.value || f.ticketNumber || f.pnr || voucher?.voucherId || 'TRV').toString();
  const codeImage = orig?.imageBase64 || '';

  return (
    <div data-testid="flight-legacy-layout">
      <div style={{ display: 'table', width: '100%', marginBottom: '16px' }}>
        <div style={{ display: 'table-cell', verticalAlign: 'top' }}>
          <SectionTitle primary={primary}>Flight Itinerary</SectionTitle>
        </div>
        <div style={{ display: 'table-cell', verticalAlign: 'top', textAlign: 'right', width: '40%' }}>
          <VoucherBarcodeStrip
            value={barcodeValue}
            imageBase64={codeImage}
            accent={accent}
            kind={codeKind}
          />
        </div>
      </div>

      {/* Route hero */}
      <div style={{
        display: 'table', width: '100%',
        padding: '20px', borderRadius: '12px', marginBottom: '24px',
        background: `${primary}14`, border: `1px solid ${primary}33`,
      }}>
        <div style={{ display: 'table-cell', verticalAlign: 'middle', width: '45%' }}>
          <div className="text-[9px] font-semibold uppercase" style={{ letterSpacing: '0.2em', color: accent }}>From</div>
          <div className="text-4xl font-extrabold tracking-tight mt-1" style={{ color: '#111827' }}>
            {fromCode || '—'}
          </div>
          <div className="text-xs mt-1" style={{ color: accent }}>{fromCity}</div>
        </div>
        <div style={{ display: 'table-cell', verticalAlign: 'middle', textAlign: 'center', width: '10%' }}>
          <FlightIcon width={36} height={36} color={primary} />
        </div>
        <div style={{ display: 'table-cell', verticalAlign: 'middle', width: '45%', textAlign: 'right' }}>
          <div className="text-[9px] font-semibold uppercase" style={{ letterSpacing: '0.2em', color: accent }}>To</div>
          <div className="text-4xl font-extrabold tracking-tight mt-1" style={{ color: '#111827' }}>
            {toCode || '—'}
          </div>
          <div className="text-xs mt-1" style={{ color: accent }}>{toCity}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <InfoCard label="Airline" value={f.airline} primary={primary} accent={accent} />
        <InfoCard label="Flight" value={f.flightNumber} primary={primary} accent={accent} mono />
        <InfoCard label="Class" value={cabinClass} primary={primary} accent={accent} />
        <InfoCard label="Departure" value={departureDT} primary={primary} accent={accent} />
        <InfoCard label="Arrival" value={arrivalDT} primary={primary} accent={accent} />
        <InfoCard label="Duration" value={f.duration} primary={primary} accent={accent} />
      </div>

      <SectionTitle primary={primary}>Passenger Details</SectionTitle>
      <div className="grid grid-cols-2 gap-3 mb-6">
        <EditableInfoCard
          label="Passenger" value={voucher?.guestName}
          fieldKey="guestName"
          editable={editable} locked={locked} onFieldSave={onFieldSave}
          primary={primary} accent={accent}
        />
        <InfoCard label="PNR" value={f.pnr} primary={primary} accent={accent} mono />
        <InfoCard label="Ticket Number" value={f.ticketNumber} primary={primary} accent={accent} mono />
        <EditableInfoCard
          label="Seat" value={f.seatNumber || 'TBA'} mono
          fieldKey="seatNumber"
          editable={editable} locked={locked} onFieldSave={onFieldSave}
          primary={primary} accent={accent}
        />
        <EditableInfoCard
          label="Baggage" value={f.baggage || 'As per airline'}
          fieldKey="baggage"
          editable={editable} locked={locked} onFieldSave={onFieldSave}
          primary={primary} accent={accent}
        />
        <EditableInfoCard
          label="Meals" value={f.mealService || ''}
          fieldKey="mealService"
          editable={editable} locked={locked} onFieldSave={onFieldSave}
          primary={primary} accent={accent}
        />
      </div>

      {(editable || locked || has(f.specialRequests)) && (
        <SectionCard title="Special Requests" icon={Heart} accent={accent} testId="flight-special-requests-card">
          <InlineEditableField
            value={f.specialRequests || ''}
            variant="text"
            editable={editable}
            locked={locked}
            onSave={(v) => onFieldSave?.('specialRequests', v)}
            textareaRows={2}
            ariaLabel="Edit special requests"
            testIdPrefix="flight-specialRequests"
            displayStyle={{ fontSize: '14px', color: '#334155' }}
          />
        </SectionCard>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TripTypePill — small uppercase chip showing one-way / round-trip / multi-city.
// ---------------------------------------------------------------------------
function TripTypePill({ tripType, primary, accent }) {
  const label = TRIP_TYPE_LABEL[(tripType || '').toLowerCase()] || (tripType || '');
  if (!label) return null;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '20px',
        background: `${primary}1f`,
        color: accent,
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        verticalAlign: 'middle',
      }}
      data-testid="flight-trip-type-pill"
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// SegmentCard — one card per flight segment.
// ---------------------------------------------------------------------------
// Layover helpers — duplicated minimally from ItineraryFlightCard so the
// voucher segment list can render the same "Plane change · 3h 20m layover
// in DEL · Change of aircraft" pill between sectors. Re-extracting to a
// shared util is a refactor we'll do once a 3rd consumer needs them.
function parseSegmentInstant(dateStr, timeStr) {
  if (!dateStr) return null;
  const t = (timeStr && /^\d{1,2}:\d{2}$/.test(timeStr)) ? timeStr : '00:00';
  const iso = `${dateStr}T${t.length === 4 ? '0' + t : t}:00`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtLayoverLabel(mins, city, code) {
  if (mins <= 0 || mins >= 1440) return null;
  const hrs = Math.floor(mins / 60);
  const m = mins % 60;
  const parts = [];
  if (hrs > 0) parts.push(`${hrs} hr`);
  if (m > 0) parts.push(`${m} mins`);
  const dur = parts.join(' ');
  const where = city ? `${city}${code ? ` (${code})` : ''}` : (code || '');
  return `${dur} layover${where ? ` in ${where}` : ''}`;
}

// LayoverPill — hr-with-centered-pill rendered between consecutive segment
// cards. `aircraftChange` (boolean) appends a small warning marker so the
// agent / customer doesn't miss "Change of aircraft" instructions.
function LayoverPill({ idx, label, aircraftChange, accent, primary }) {
  return (
    <div
      data-testid={`flight-segment-layover-${idx}`}
      style={{
        position: 'relative',
        margin: '14px 0',
        textAlign: 'center',
      }}
    >
      <hr
        style={{
          border: 'none',
          borderTop: '1px dashed #E5E7EB',
          margin: 0,
          height: 0,
        }}
      />
      <div
        style={{
          position: 'relative',
          display: 'inline-block',
          marginTop: '-12px',
          padding: '4px 12px',
          background: '#FFFFFF',
          border: `1px solid ${aircraftChange ? '#F59E0B' : '#E5E7EB'}`,
          borderRadius: '999px',
          fontSize: '11px',
          color: accent,
          fontWeight: 600,
          letterSpacing: '0.04em',
        }}
      >
        {label}
        {aircraftChange && (
          <>
            <span aria-hidden="true" style={{ margin: '0 6px', color: '#F59E0B' }}>·</span>
            <span
              data-testid={`flight-segment-layover-${idx}-aircraft-change`}
              style={{ color: '#B45309', fontWeight: 700 }}
            >
              Change of aircraft
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function SegmentCard({ seg, idx, primary, accent, isLast }) {
  const flightCode = [seg.airlineCode, seg.flightNumber].filter(Boolean).join(' ');
  const headerTitle = `Segment ${seg.segmentNumber || idx + 1} · ${seg.airline || ''}${flightCode ? '  ·  ' + flightCode : ''}`.trim();
  // Friendly airport detail line — normalises "DELHI INDRA GANDHI INTL" →
  // "Delhi, Indra Gandhi Intl Airport". Only render when we have a distinct
  // IATA code, so the IATA-headline column doesn't duplicate the name on
  // two lines.
  const depAirportDetail = seg.departureAirportCode
    ? [formatAirportName(seg.departureAirport), seg.departureAirportCode].filter(Boolean).join(' · ')
    : '';
  const arrAirportDetail = seg.arrivalAirportCode
    ? [formatAirportName(seg.arrivalAirport), seg.arrivalAirportCode].filter(Boolean).join(' · ')
    : '';
  // Issue 3 (revised): hide baggageCabin display whenever it exactly equals
  // baggageCheckIn. The previous extra `has(baggageAdult)` clause incorrectly
  // assumed a populated per-pax-type field as a hallucination signal — but
  // many domestic airlines (Air India, IndiGo) print only one allowance and
  // never populate baggageAdult, so the guard never fired. Equality alone is
  // enough: cabin is realistically always smaller than check-in.
  const hideCabin =
    has(seg.baggageCabin)
    && seg.baggageCabin.trim() === (seg.baggageCheckIn || '').trim();
  const showCabin = has(seg.baggageCabin) && !hideCabin;
  // Layout A — render the segment as a borderless block. Card chrome (bg /
  // border / radius) is replaced by an `<hr>` divider rendered after every
  // segment except the last by the parent map.
  return (
    <section
      data-testid={`flight-segment-card-${idx}`}
      className="break-inside-avoid pdf-no-break"
      style={{
        display: 'block',
        width: '100%',
        padding: '8px 0',
        marginBottom: '8px',
        pageBreakInside: 'avoid',
        breakInside: 'avoid',
      }}
    >
      {/* Header line — segment title + flight code. No icon to keep things
          minimal; the route hero above already carries the FlightIcon. */}
      <h3
        className="uppercase font-semibold"
        style={{
          fontSize: '12px',
          letterSpacing: '0.08em',
          color: accent,
          marginBottom: '14px',
        }}
      >
        {headerTitle}
      </h3>

      <div style={{ display: 'table', width: '100%', tableLayout: 'fixed' }}>
        <div style={{ display: 'table-cell', verticalAlign: 'top', paddingRight: '12px', width: '45%' }}>
          <div className="text-[9px] uppercase" style={{ letterSpacing: '0.18em', color: accent, fontWeight: 600 }}>Departure</div>
          {seg.departureAirportCode ? (
            <div style={{ fontSize: '32px', fontWeight: 800, lineHeight: 1.05, color: '#111827', marginTop: '4px' }}>
              {seg.departureAirportCode}
            </div>
          ) : (
            <div
              style={{
                fontSize: '14px', fontWeight: 600, color: '#111827',
                marginTop: '4px', lineHeight: 1.2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
              title={seg.departureCity || ''}
            >
              {(seg.departureCity || '—').slice(0, 20)}
            </div>
          )}
          {seg.departureAirportCode && (
            <div className="text-xs mt-1" style={{ color: accent }}>{seg.departureCity || ''}</div>
          )}
          <div className="text-sm mt-2" style={{ color: '#111827', fontWeight: 600 }}>
            {fmtDateTime(seg.departureDate, seg.departureTime) || '—'}
          </div>
          {has(seg.checkInCloseTime) && (
            <div
              className="text-[10px] mt-1 uppercase"
              style={{ letterSpacing: '0.12em', color: '#B91C1C', fontWeight: 600 }}
              data-testid={`flight-segment-${idx}-checkin-close`}
            >
              Check-in closes · {seg.checkInCloseTime}
            </div>
          )}
          {depAirportDetail && <div className="text-[11px] mt-1" style={{ color: accent }}>{depAirportDetail}</div>}
          {seg.departureTerminal && (
            <div className="text-[11px] mt-0.5" style={{ color: accent }}>Terminal {seg.departureTerminal}</div>
          )}
        </div>
        <div style={{ display: 'table-cell', verticalAlign: 'middle', textAlign: 'center', width: '10%' }}>
          <FlightIcon width={24} height={24} color={primary} />
        </div>
        <div style={{ display: 'table-cell', verticalAlign: 'top', paddingLeft: '12px', width: '45%', textAlign: 'right' }}>
          <div className="text-[9px] uppercase" style={{ letterSpacing: '0.18em', color: accent, fontWeight: 600 }}>Arrival</div>
          {seg.arrivalAirportCode ? (
            <div style={{ fontSize: '32px', fontWeight: 800, lineHeight: 1.05, color: '#111827', marginTop: '4px' }}>
              {seg.arrivalAirportCode}
            </div>
          ) : (
            <div
              style={{
                fontSize: '14px', fontWeight: 600, color: '#111827',
                marginTop: '4px', lineHeight: 1.2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
              title={seg.arrivalCity || ''}
            >
              {(seg.arrivalCity || '—').slice(0, 20)}
            </div>
          )}
          {seg.arrivalAirportCode && (
            <div className="text-xs mt-1" style={{ color: accent }}>{seg.arrivalCity || ''}</div>
          )}
          <div className="text-sm mt-2" style={{ color: '#111827', fontWeight: 600 }}>
            {fmtDateTime(seg.arrivalDate, seg.arrivalTime) || '—'}
          </div>
          {arrAirportDetail && <div className="text-[11px] mt-1" style={{ color: accent }}>{arrAirportDetail}</div>}
          {seg.arrivalTerminal && (
            <div className="text-[11px] mt-0.5" style={{ color: accent }}>Terminal {seg.arrivalTerminal}</div>
          )}
        </div>
      </div>

      {/* Plain-text info row — no boxed InfoCards. Class · Duration · Status. */}
      <div
        style={{
          marginTop: '14px',
          fontSize: '12px',
          color: '#334155',
          lineHeight: 1.6,
        }}
      >
        <span style={{ marginRight: '18px' }}>
          <span className="uppercase" style={{ fontSize: '10px', letterSpacing: '0.12em', color: accent, fontWeight: 600, marginRight: '6px' }}>Class</span>
          {seg.travelClass || 'Economy'}
        </span>
        {has(seg.duration) && (
          <span style={{ marginRight: '18px' }}>
            <span className="uppercase" style={{ fontSize: '10px', letterSpacing: '0.12em', color: accent, fontWeight: 600, marginRight: '6px' }}>Duration</span>
            {seg.duration}
          </span>
        )}
        <span style={{ marginRight: '18px' }}>
          <span className="uppercase" style={{ fontSize: '10px', letterSpacing: '0.12em', color: accent, fontWeight: 600, marginRight: '6px' }}>Status</span>
          {seg.status || 'Confirmed'}
        </span>
      </div>

      {(has(seg.aircraft) || has(seg.baggageCheckIn) || showCabin || has(seg.mealService)) && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#334155', lineHeight: 1.6 }}>
          {has(seg.aircraft) && (
            <span style={{ marginRight: '18px' }}>
              <span className="uppercase" style={{ fontSize: '10px', letterSpacing: '0.12em', color: accent, fontWeight: 600, marginRight: '6px' }}>Aircraft</span>
              {seg.aircraft}
            </span>
          )}
          {has(seg.mealService) && (
            <span style={{ marginRight: '18px' }}>
              <span className="uppercase" style={{ fontSize: '10px', letterSpacing: '0.12em', color: accent, fontWeight: 600, marginRight: '6px' }}>Meal</span>
              {seg.mealService}
            </span>
          )}
        </div>
      )}

      {/* Baggage block — two separate labeled rows. Check-in and Cabin
          are rendered independently when each value exists. Pass 3 fix
          replaces the old inline "Check-in X · Cabin Y" single-line
          format which made it hard to read at a glance. */}
      {(has(seg.baggageCheckIn) || showCabin) && (
        <div
          data-testid={`flight-segment-${idx}-baggage`}
          style={{ marginTop: '8px', fontSize: '12px', color: '#334155', lineHeight: 1.7 }}
        >
          {has(seg.baggageCheckIn) && (
            <div>
              <span className="uppercase" style={{ fontSize: '10px', letterSpacing: '0.12em', color: accent, fontWeight: 600, marginRight: '8px', minWidth: '64px', display: 'inline-block' }}>
                Check-in
              </span>
              {seg.baggageCheckIn}
            </div>
          )}
          {showCabin && (
            <div>
              <span className="uppercase" style={{ fontSize: '10px', letterSpacing: '0.12em', color: accent, fontWeight: 600, marginRight: '8px', minWidth: '64px', display: 'inline-block' }}>
                Cabin
              </span>
              {seg.baggageCabin}
            </div>
          )}
        </div>
      )}

      {/* Layout A divider between segments */}
      {!isLast && (
        <hr
          style={{
            border: 'none',
            borderTop: '1px solid #F3F4F6',
            margin: '16px 0 0 0',
          }}
          aria-hidden="true"
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// PassengerCard — editable name + special meal + special assistance grid.
// Each segment is rendered as one "sector block" — left cell carries the
// sector codes ("CCU – BOM"), flight code, editable seat, and SSR chips
// (meal + assistance) inline; right cell holds the per-segment barcode/QR.
// Barcode → passenger mapping uses Option B index formula.
// ---------------------------------------------------------------------------
function PassengerCard({
  passenger, pIdx, segments, allCodes,
  primary, accent, editable, locked, onFieldSave,
  // Agent-only action surface — when both are truthy, the trash icon
  // (top-right of the card header) and per-sector "Upload barcode" buttons
  // are rendered. `onRefresh` is called after a successful mutation so the
  // parent screen re-fetches the voucher and the UI reflects the change.
  voucherId, onRefresh, totalPassengers,
}) {
  const p = passenger || {};
  const perSeg = Array.isArray(p.perSegment) ? p.perSegment : [];
  const displayName = p.fullName || [p.title, p.firstName, p.lastName].filter(Boolean).join(' ') || `Passenger ${pIdx + 1}`;
  const fileInputRefs = useRef({});
  const [uploadingSector, setUploadingSector] = useState(null);

  const showAgentActions = editable && !locked && voucherId && BACKEND_URL;
  const canDelete = showAgentActions && (totalPassengers > 1);

  const handleDeletePassenger = async () => {
    if (!showAgentActions) return;
    const confirmed = window.confirm(
      `Remove ${displayName} from this voucher? This cannot be undone.`,
    );
    if (!confirmed) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/vouchers/${voucherId}/passenger/${pIdx}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      onRefresh?.();
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert(`Delete failed: ${err?.response?.data?.detail || err.message}`);
    }
  };

  const handleBarcodeUpload = async (sIdx, file) => {
    if (!showAgentActions || !file) return;
    try {
      setUploadingSector(sIdx);
      const fd = new FormData();
      fd.append('passenger_idx', String(pIdx));
      fd.append('segment_idx', String(sIdx));
      fd.append('file', file);
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/vouchers/${voucherId}/manual-barcode`,
        fd,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      onRefresh?.();
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert(`Upload failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setUploadingSector(null);
    }
  };

  const handleDeleteManualCode = async (sIdx) => {
    if (!showAgentActions) return;
    const confirmed = window.confirm('Remove this manually-uploaded barcode? The renderer will fall back to the auto-detected barcode (if any).');
    if (!confirmed) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/vouchers/${voucherId}/manual-barcode`,
        {
          params: { passenger_idx: pIdx, segment_idx: sIdx },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      onRefresh?.();
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert(`Delete failed: ${err?.response?.data?.detail || err.message}`);
    }
  };

  return (
    <SectionCard
      title={`Passenger ${pIdx + 1}`}
      icon={User}
      accent={accent}
      testId={`flight-passenger-card-${pIdx}`}
      className="mb-4"
      bordered={false}
      headerAction={canDelete ? (
        <button
          type="button"
          data-testid={`flight-passenger-${pIdx}-delete-btn`}
          onClick={handleDeletePassenger}
          aria-label={`Remove passenger ${pIdx + 1}`}
          className="agent-toolbar"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            border: '1px solid #FCA5A5',
            background: '#FEF2F2',
            color: '#B91C1C',
            borderRadius: '8px',
            fontSize: '11px',
            fontWeight: 600,
            cursor: 'pointer',
            letterSpacing: '0.04em',
            lineHeight: 1,
          }}
        >
          <Trash2 style={{ width: '14px', height: '14px' }} />
          <span>Remove</span>
        </button>
      ) : null}
    >
      <div style={{ marginBottom: '10px' }}>
        <div className="text-[10px] uppercase" style={{ letterSpacing: '0.18em', color: accent, fontWeight: 600 }}>Name</div>
        <div className="mt-1" style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>
          <InlineEditableField
            value={displayName}
            variant="text"
            editable={editable}
            locked={locked}
            onSave={(v) => onFieldSave?.(`flight.passengers.${pIdx}.fullName`, v)}
            textareaRows={1}
            ariaLabel={`Edit passenger ${pIdx + 1} name`}
            testIdPrefix={`flight-passenger-${pIdx}-name`}
            displayStyle={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}
          />
        </div>
        {/* Per-passenger e-ticket number, rendered under the name. Mono +
            muted accent + tight letter-spacing — looks like a coupon ref,
            not a header banner. Hidden when empty. */}
        {has(p.eTicketNumber) && (
          <div
            style={{ marginTop: '4px', fontSize: '11px', color: accent }}
            data-testid={`flight-passenger-${pIdx}-eticket`}
          >
            <span
              className="uppercase"
              style={{ fontWeight: 600, letterSpacing: '0.12em', marginRight: '6px' }}
            >
              E-Ticket
            </span>
            <span className="font-mono">{p.eTicketNumber}</span>
          </div>
        )}
        {/* Per-booking contact line — shown ONLY under Passenger 1 (the lead).
            E-tickets typically print ONE booking-level contact, so rendering it
            under every passenger creates the false impression that each pax has
            their own number. Phase 15.6 — restrict to pIdx === 0. */}
        {pIdx === 0 && (editable || has(p.mobile) || has(p.email)) && (
          <div
            data-testid={`flight-passenger-${pIdx}-contact`}
            style={{
              marginTop: '4px',
              fontSize: '11px',
              color: accent,
              display: 'inline-flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '6px',
              lineHeight: 1.4,
            }}
          >
            <InlineEditableField
              value={p.mobile || ''}
              variant="text"
              editable={editable}
              locked={locked}
              placeholder="+91 XXXXXXXXXX"
              onSave={(v) => onFieldSave?.(`flight.passengers.${pIdx}.mobile`, v)}
              textareaRows={1}
              ariaLabel={`Edit passenger ${pIdx + 1} mobile`}
              testIdPrefix={`flight-passenger-${pIdx}-mobile`}
              displayStyle={{ fontSize: '11px', color: accent }}
            />
            {(has(p.mobile) || editable) && (has(p.email) || editable) && (
              <span aria-hidden="true">·</span>
            )}
            <InlineEditableField
              value={p.email || ''}
              variant="text"
              editable={editable}
              locked={locked}
              placeholder="name@example.com"
              onSave={(v) => onFieldSave?.(`flight.passengers.${pIdx}.email`, v)}
              textareaRows={1}
              ariaLabel={`Edit passenger ${pIdx + 1} email`}
              testIdPrefix={`flight-passenger-${pIdx}-email`}
              displayStyle={{ fontSize: '11px', color: accent }}
            />
          </div>
        )}
        {(has(p.type) || has(p.dateOfBirth) || has(p.passportNumber) || has(p.nationality) || has(p.frequentFlyer) || has(p.ffNumber)) && (
          <div className="text-[11px] mt-1.5" style={{ color: accent }}>
            {[
              normalisePaxType(p.type),
              p.dateOfBirth && `DOB ${formatVoucherDate(p.dateOfBirth)}`,
              p.passportNumber && `Passport ${p.passportNumber}`,
              p.nationality,
              // FF membership — labelled "FF" so it doesn't look like a stray
              // code. Accept either `frequentFlyer` (legacy) or `ffNumber`
              // (newer extractions). Renders ONLY when present.
              (p.frequentFlyer || p.ffNumber) && `FF ${p.frequentFlyer || p.ffNumber}`,
            ].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>

      {segments.length > 0 && (
        <div data-testid={`flight-passenger-${pIdx}-sectors`}>
          {segments.map((seg, sIdx) => {
            const ps = perSeg.find((x) => x.segmentNumber === (seg.segmentNumber || sIdx + 1)) || perSeg[sIdx] || {};
            const code = getPassengerCode(pIdx, sIdx, segments.length, allCodes);
            const sectorCodes = [seg.departureAirportCode, seg.arrivalAirportCode]
              .filter(Boolean).join(' – ')
              || `Seg ${seg.segmentNumber || sIdx + 1}`;
            const flightCode = [seg.airlineCode, seg.flightNumber].filter(Boolean).join(' ');
            const isLastSector = sIdx === segments.length - 1;
            return (
              <div
                key={sIdx}
                data-testid={`flight-passenger-${pIdx}-sector-${sIdx}`}
                className="break-inside-avoid pdf-no-break"
                style={{
                  display: 'table',
                  width: '100%',
                  padding: '8px 0',
                  marginBottom: isLastSector ? '0' : '0',
                  borderBottom: isLastSector ? 'none' : '1px solid #F3F4F6',
                  tableLayout: 'fixed',
                  pageBreakInside: 'avoid',
                }}
              >
                {/* LEFT cell — sector codes + flight code + seat + SSR chips */}
                <div style={{ display: 'table-cell', verticalAlign: 'middle', width: '55%', paddingRight: '12px' }}>
                  {/* Line 1: CCU – BOM  6E 2341 */}
                  <div style={{ marginBottom: '6px', lineHeight: 1.2 }}>
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#111827', marginRight: '8px' }}>
                      {sectorCodes}
                    </span>
                    {flightCode && (
                      <span
                        className="font-mono"
                        style={{ fontSize: '11px', color: accent, fontWeight: 600 }}
                      >
                        {flightCode}
                      </span>
                    )}
                  </div>
                  {/* Line 2: Seat (label + value) + SSR chips inline */}
                  <div style={{ lineHeight: 1.3 }}>
                    <span
                      className="text-[9px] uppercase"
                      style={{ letterSpacing: '0.18em', color: accent, fontWeight: 600, marginRight: '6px', verticalAlign: 'middle' }}
                    >
                      Seat
                    </span>
                    {(() => {
                      const seatRaw = (ps.seatNumber || '').trim();
                      const hasSeat = !!seatRaw && seatRaw.toUpperCase() !== 'TBA';
                      // View-mode for assigned seats: bold mono. View-mode for
                      // unassigned: light-grey "TBA" placeholder, NO edit box —
                      // agent has to click the chip to enter edit mode.
                      if (!editable) {
                        return (
                          <span
                            className="font-mono"
                            style={{
                              fontSize: hasSeat ? '14px' : '12px',
                              fontWeight: hasSeat ? 700 : 400,
                              color: hasSeat ? '#111827' : '#9CA3AF',
                              fontStyle: hasSeat ? 'normal' : 'italic',
                              verticalAlign: 'middle',
                            }}
                            data-testid={`flight-passenger-${pIdx}-seg-${sIdx}-seat`}
                          >
                            {hasSeat ? seatRaw : 'TBA'}
                          </span>
                        );
                      }
                      return (
                        <InlineEditableField
                          value={seatRaw}
                          variant="text"
                          editable={editable}
                          locked={locked}
                          placeholder="TBA"
                          onSave={(v) => onFieldSave?.(`flight.passengers.${pIdx}.perSegment.${sIdx}.seatNumber`, v)}
                          textareaRows={1}
                          ariaLabel={`Edit passenger ${pIdx + 1} seat for segment ${sIdx + 1}`}
                          testIdPrefix={`flight-passenger-${pIdx}-seg-${sIdx}-seat`}
                          displayClassName={hasSeat ? 'font-mono text-sm font-bold' : 'font-mono text-xs italic'}
                          displayStyle={{ color: hasSeat ? '#111827' : '#9CA3AF' }}
                        />
                      );
                    })()}
                    {has(ps.specialMeal) && (
                      <SsrChip code={ps.specialMeal} primary={primary} accent={accent} />
                    )}
                    {has(ps.specialAssistance) && (
                      <SsrChip code={ps.specialAssistance} primary={primary} accent={accent} />
                    )}
                    {has(ps.specialAssistance2) && (
                      <SsrChip code={ps.specialAssistance2} primary={primary} accent={accent} />
                    )}
                    {(() => {
                      // Senior-citizen pax (IATA "SRCT" code OR pax.type
                      // contains "SR"/"Senior") gets an extra SSR slot so
                      // agents can stack e.g. SRCT + WCHR on the same leg.
                      const paxTypeStr = String(p.type || '').toUpperCase();
                      const isSenior = (
                        paxTypeStr.includes('SENIOR')
                        || paxTypeStr === 'SR'
                        || paxTypeStr === 'SRCT'
                        || String(ps.specialAssistance || '').toUpperCase() === 'SRCT'
                      );
                      const slot1Open = !has(ps.specialAssistance);
                      const slot2Open = isSenior && !has(ps.specialAssistance2);
                      const showMealAdd = editable && !locked && !has(ps.specialMeal);
                      const showSsrAdd = editable && !locked && (slot1Open || slot2Open);
                      if (!showMealAdd && !showSsrAdd) return null;
                      // Wire onSave to the FIRST open assistance slot. When
                      // both slot1 and slot2 are open, the FIRST press fills
                      // slot1; the next press fills slot2 (because slot1Open
                      // flips to false on next render).
                      const ssrTargetIdx = slot1Open ? 1 : 2;
                      const ssrField = ssrTargetIdx === 1
                        ? 'specialAssistance'
                        : 'specialAssistance2';
                      return (
                        <span
                          className="agent-toolbar"
                          style={{ marginLeft: '8px', display: 'inline-flex', gap: '6px', verticalAlign: 'middle' }}
                        >
                          {showMealAdd && (
                            <SsrSeedButton
                              kind="meal"
                              primary={primary}
                              accent={accent}
                              onSave={(v) => onFieldSave?.(`flight.passengers.${pIdx}.perSegment.${sIdx}.specialMeal`, v)}
                              testId={`flight-passenger-${pIdx}-seg-${sIdx}-add-meal`}
                            />
                          )}
                          {showSsrAdd && (
                            <SsrSeedButton
                              kind="ssr"
                              primary={primary}
                              accent={accent}
                              onSave={(v) => onFieldSave?.(`flight.passengers.${pIdx}.perSegment.${sIdx}.${ssrField}`, v)}
                              testId={`flight-passenger-${pIdx}-seg-${sIdx}-add-ssr${ssrTargetIdx === 2 ? '-2' : ''}`}
                            />
                          )}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                {/* RIGHT cell — barcode flush right, vertically centered */}
                <div
                  data-testid={`flight-passenger-${pIdx}-seg-${sIdx}-code`}
                  style={{ display: 'table-cell', verticalAlign: 'middle', textAlign: 'right', width: '45%' }}
                >
                  {code && (
                    <VoucherBarcodeStrip
                      value={code.value}
                      imageBase64={code.imageBase64}
                      kind={code.kind || 'qr'}
                      accent={accent}
                    />
                  )}
                  {showAgentActions && (
                    <div
                      className="agent-toolbar"
                      style={{ marginTop: '6px', textAlign: 'right', lineHeight: 1 }}
                    >
                      <input
                        ref={(el) => { fileInputRefs.current[sIdx] = el; }}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        style={{ display: 'none' }}
                        data-testid={`flight-passenger-${pIdx}-seg-${sIdx}-upload-input`}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleBarcodeUpload(sIdx, f);
                          if (e.target) e.target.value = '';
                        }}
                      />
                      <button
                        type="button"
                        data-testid={`flight-passenger-${pIdx}-seg-${sIdx}-upload-btn`}
                        onClick={() => fileInputRefs.current[sIdx]?.click()}
                        disabled={uploadingSector === sIdx}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          border: '1px solid #E5E7EB',
                          background: '#FFFFFF',
                          color: '#374151',
                          borderRadius: '6px',
                          fontSize: '10px',
                          fontWeight: 600,
                          cursor: uploadingSector === sIdx ? 'wait' : 'pointer',
                          letterSpacing: '0.04em',
                        }}
                      >
                        <Upload style={{ width: '11px', height: '11px' }} />
                        <span>{uploadingSector === sIdx ? 'Uploading…' : (code?.format === 'MANUAL' ? 'Replace' : 'Upload')}</span>
                      </button>
                      {code?.format === 'MANUAL' && (
                        <button
                          type="button"
                          data-testid={`flight-passenger-${pIdx}-seg-${sIdx}-delete-manual-btn`}
                          onClick={() => handleDeleteManualCode(sIdx)}
                          aria-label={`Remove manual barcode for sector ${sIdx + 1}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 6px',
                            border: '1px solid #FCA5A5',
                            background: '#FFFFFF',
                            color: '#B91C1C',
                            borderRadius: '6px',
                            fontSize: '10px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            marginLeft: '6px',
                          }}
                        >
                          <X style={{ width: '11px', height: '11px' }} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* SSR chips (Special Meal + Special Assistance) are rendered inline
          on the sector row above next to the seat number — see the per-
          segment block. There is intentionally NO duplicated edit grid at
          the bottom of the card; populate SSR via the chip area when
          editable, or via the underlying voucher PATCH endpoint. */}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// FareDetailsCard — divider-style layout (no box). Renders TWO modes:
//
//   AGENT view (isAgentView=true, used in VoucherPreview)
//     Base Fare    ₹7,420
//     Taxes        ₹1,860            ← supplier taxes as printed
//     Markup       ₹340  [Fixed ▾]   ← inline editable amount + type select
//     ────────────────────
//     Total        ₹9,620            ← base + taxes + markup
//
//   CUSTOMER view (isAgentView=false, used in PublicVoucher)
//     Base Fare    ₹7,420
//     Taxes        ₹2,200            ← supplier taxes + markup, no breakdown
//     ────────────────────
//     Total        ₹9,620
//
//   Markup never appears in customer view. Total is always computed at
//   render time — never persisted.
//
// Markup amount is editable inline via onFieldSave('flight.markup_amount', n)
// with optional onFieldSave('flight.markup_type', 'fixed'|'percentage').
// ---------------------------------------------------------------------------
function FareRow({ label, value, customValue, accent, bold = false }) {
  return (
    <div style={{ display: 'table', width: '100%', tableLayout: 'fixed', marginBottom: '6px' }}>
      <div style={{ display: 'table-cell', verticalAlign: 'middle', width: '40%', paddingRight: '14px' }}>
        <span
          className="uppercase"
          style={{
            fontSize: '11px',
            letterSpacing: '0.1em',
            color: accent,
            fontWeight: bold ? 700 : 600,
          }}
        >
          {label}
        </span>
      </div>
      <div
        style={{
          display: 'table-cell',
          verticalAlign: 'middle',
          textAlign: 'right',
          fontSize: bold ? '15px' : '13px',
          color: '#111827',
          fontWeight: bold ? 800 : 600,
        }}
      >
        {customValue || value}
      </div>
    </div>
  );
}

function FareDetailsCard({
  fare,
  markup,
  accent,
  isAgentView = false,
  editable = false,
  locked = false,
  onFieldSave,
}) {
  if (!fare || typeof fare !== 'object') return null;
  const baseFare = toNum(fare.baseFare);

  // Taxes — nested object (new schema) OR flat string (legacy).
  // When nested, individual sub-fields drive the agent breakdown.
  const taxesObj = fare.taxes;
  const isNested = taxesObj && typeof taxesObj === 'object' && !Array.isArray(taxesObj);
  const k3 = isNested ? toNum(taxesObj.k3Tax) : 0;
  const otherTax = isNested ? toNum(taxesObj.otherTaxes) : 0;
  const ancillary = isNested ? toNum(taxesObj.ancillaryFare) : 0;
  const serviceFee = isNested ? toNum(taxesObj.serviceFee) : 0;
  const discount = isNested ? toNum(taxesObj.discount) : 0;
  const totalTaxes = isNested
    ? (toNum(taxesObj.totalTaxes) || (k3 + otherTax + ancillary + serviceFee - discount))
    : toNum(taxesObj);

  const markupAmount = toNum(markup?.amount);
  const markupType = markup?.type || 'fixed';
  const markupValue = markupType === 'percentage'
    ? (baseFare * markupAmount) / 100
    : markupAmount;

  // Grand total prefers explicit `grandTotal` (new), falls back to legacy
  // `totalFare`, and only synthesises base + taxes when neither was printed.
  const grandTotalRaw = toNum(fare.grandTotal)
    || toNum(fare.totalFare)
    || (baseFare + totalTaxes);

  const taxesForCustomer = totalTaxes + markupValue;
  const totalForCustomer = baseFare + taxesForCustomer;

  // Hide entire card when there's no fare data AND we're in customer view.
  // Agent view always renders so a markup can be set even without a
  // printed fare breakdown — provided the agent has edit rights.
  const hasSupplierData = baseFare !== 0 || totalTaxes !== 0 || grandTotalRaw !== 0;
  if (!isAgentView && !hasSupplierData && markupValue === 0) return null;
  if (isAgentView && !hasSupplierData && !editable && !locked) return null;

  const curr = (fare.currency || 'INR').toUpperCase();
  const SYMBOL = {
    INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'AED ', SGD: 'S$', AUD: 'A$',
  };
  const symbol = SYMBOL[curr] || `${curr} `;
  const fmt = (n) => (Number.isFinite(n) ? `${symbol}${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—');

  return (
    <section
      data-testid="flight-fare-card"
      className="break-inside-avoid pdf-no-break"
      style={{ marginTop: '16px', pageBreakInside: 'avoid' }}
    >
      <h3
        className="uppercase font-semibold"
        style={{
          fontSize: '12px',
          letterSpacing: '0.08em',
          color: accent,
          marginBottom: '12px',
        }}
      >
        Fare Details
      </h3>
      <div>
        <FareRow label="Base Fare" value={fmt(baseFare)} accent={accent} />
        {isAgentView ? (
          // Agent view — full tax breakdown. K3 and Other Taxes always
          // render (with `—` fallback) so the agent can see when the LLM
          // failed to break the taxes line down. Optional fields (ancillary,
          // service fee, discount) stay conditional since they're often
          // genuinely absent rather than missing-due-to-extraction-error.
          <>
            <FareRow
              label="K3 Tax"
              value={k3 !== 0 ? fmt(k3) : '—'}
              accent={accent}
            />
            <FareRow
              label="Other Taxes"
              value={otherTax !== 0 ? fmt(otherTax) : '—'}
              accent={accent}
            />
            {ancillary !== 0 && <FareRow label="Ancillary Fare" value={fmt(ancillary)} accent={accent} />}
            {serviceFee !== 0 && <FareRow label="Service Fee" value={fmt(serviceFee)} accent={accent} />}
            {discount !== 0 && <FareRow label="Discount" value={`- ${fmt(discount)}`} accent={accent} />}
            <FareRow
              label="Markup"
              accent={accent}
              customValue={(
                <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
                  <InlineEditableField
                    value={String(markupAmount || 0)}
                    variant="text"
                    editable={editable && !locked}
                    locked={locked}
                    onSave={(v) => onFieldSave?.('flight.markup_amount', toNum(v))}
                    textareaRows={1}
                    ariaLabel="Edit markup amount"
                    testIdPrefix="flight-markup-amount"
                    displayClassName="font-mono font-bold"
                    displayStyle={{ color: '#111827' }}
                  />
                  <select
                    value={markupType}
                    onChange={(e) => onFieldSave?.('flight.markup_type', e.target.value)}
                    disabled={!editable || locked}
                    data-testid="flight-markup-type-select"
                    style={{
                      fontSize: '11px',
                      padding: '2px 6px',
                      marginLeft: '8px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '4px',
                      background: '#fff',
                      color: accent,
                      cursor: editable && !locked ? 'pointer' : 'default',
                    }}
                  >
                    <option value="fixed">Fixed</option>
                    <option value="percentage">Percentage</option>
                  </select>
                  {markupType === 'percentage' && markupValue > 0 && (
                    <span
                      style={{ marginLeft: '10px', fontSize: '11px', color: accent }}
                      data-testid="flight-markup-computed"
                    >
                      = {fmt(markupValue)}
                    </span>
                  )}
                </span>
              )}
            />
          </>
        ) : (
          // Customer view — single combined Taxes line (silently includes markup).
          <FareRow label="Taxes" value={fmt(taxesForCustomer)} accent={accent} />
        )}
        <hr
          style={{ border: 'none', borderTop: '1px solid #E5E7EB', margin: '10px 0 8px 0' }}
          aria-hidden="true"
        />
        <FareRow
          label="Grand Total"
          value={fmt(isAgentView ? (baseFare + totalTaxes + markupValue) : totalForCustomer)}
          accent={accent}
          bold
        />
      </div>
      {/* Agent-only markup note. Hidden in customer view entirely. */}
      {isAgentView && (editable || locked || has(markup?.note)) && (
        <div style={{ marginTop: '10px' }} data-testid="flight-markup-note-row">
          <span
            className="uppercase"
            style={{
              fontSize: '10px',
              letterSpacing: '0.14em',
              color: accent,
              fontWeight: 600,
              marginRight: '8px',
            }}
          >
            Markup Note
          </span>
          <InlineEditableField
            value={markup?.note || ''}
            variant="text"
            editable={editable && !locked}
            locked={locked}
            onSave={(v) => onFieldSave?.('flight.markup_note', v)}
            textareaRows={1}
            ariaLabel="Edit markup note"
            testIdPrefix="flight-markup-note"
            displayStyle={{ fontSize: '12px', color: '#475569', fontStyle: 'italic' }}
          />
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// PoliciesCard — divider-separated label/value rows. Always customer-visible
// (i.e. printed on the public PDF). Each row only renders when its value is
// non-empty; the entire card hides when every field is empty. Layout A
// styling — no box, just dividers between rows, label in small muted caps.
// ---------------------------------------------------------------------------
function PoliciesCard({ policies, accent }) {
  if (!policies || typeof policies !== 'object') return null;
  const rows = [
    { label: 'Fare Rules', value: policies.fareRules },
    { label: 'Cancellation', value: policies.cancellation },
    { label: 'Changes', value: policies.changes },
    { label: 'Baggage Allowance', value: policies.baggageAllowanceDetail },
    { label: 'Baggage Rules', value: policies.baggageRules },
    { label: 'Check-in', value: policies.checkInPolicy },
    { label: 'Documents Required', value: policies.documentsRequired },
    { label: 'Important Notes', value: policies.importantNotes },
  ].filter((r) => has(r.value));
  if (rows.length === 0) return null;
  return (
    <section
      data-testid="flight-policies-card"
      className="break-inside-avoid pdf-no-break"
      style={{ marginTop: '20px', pageBreakInside: 'avoid' }}
    >
      <h3
        className="uppercase font-semibold"
        style={{
          fontSize: '12px',
          letterSpacing: '0.08em',
          color: accent,
          marginBottom: '14px',
        }}
      >
        Policies &amp; Important Notes
      </h3>
      <div>
        {rows.map((r, i) => (
          <React.Fragment key={r.label}>
            <div
              data-testid={`flight-policies-row-${r.label.toLowerCase().replace(/\s+/g, '-')}`}
              style={{ display: 'table', width: '100%', tableLayout: 'fixed' }}
            >
              <div
                style={{
                  display: 'table-cell',
                  verticalAlign: 'top',
                  width: '170px',
                  paddingRight: '14px',
                }}
              >
                <span
                  className="uppercase"
                  style={{
                    fontSize: '10px',
                    letterSpacing: '0.14em',
                    color: accent,
                    fontWeight: 600,
                  }}
                >
                  {r.label}
                </span>
              </div>
              <div
                style={{
                  display: 'table-cell',
                  verticalAlign: 'top',
                  fontSize: '13px',
                  color: '#334155',
                  lineHeight: 1.55,
                  whiteSpace: 'pre-line',
                }}
              >
                {r.value}
              </div>
            </div>
            {i < rows.length - 1 && (
              <hr
                style={{ border: 'none', borderTop: '1px solid #F3F4F6', margin: '10px 0' }}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// PassengerCapWarning — banner shown when passengers > PASSENGERS_PER_PDF_CAP.
// ---------------------------------------------------------------------------
function PassengerCapWarning({ total, accent }) {
  return (
    <div
      data-testid="flight-passenger-cap-warning"
      style={{
        display: 'table', width: '100%',
        padding: '10px 14px',
        marginBottom: '14px',
        background: '#FEF3C7',
        border: '1px solid #FCD34D',
        borderRadius: '8px',
      }}
    >
      <div style={{ display: 'table-cell', verticalAlign: 'middle', width: '24px', paddingRight: '10px' }}>
        <AlertTriangle style={{ width: '18px', height: '18px', color: '#B45309', display: 'block' }} />
      </div>
      <div style={{ display: 'table-cell', verticalAlign: 'middle', fontSize: '12px', color: '#92400E', lineHeight: 1.4 }}>
        <strong>Only first {PASSENGERS_PER_PDF_CAP} of {total} passengers shown.</strong>{' '}
        Download individual vouchers for remaining passengers.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FareRulesCard — agent toggle + (auto-fetch | paste text | manual edit).
// Visibility is controlled by `flight.showFareRules` (boolean toggle stored
// on the voucher). When the toggle is OFF the card collapses to a thin
// switch in the agent view, and renders NOTHING in customer / PDF view.
// When ON it renders the rules as either bullet list (array form) or a
// paragraph (string form).
// ---------------------------------------------------------------------------
function FareRulesCard({
  fareRules,
  showFareRules,
  accent,
  primary,
  editable,
  locked,
  voucherId,
  onRefresh,
  hasSourcePdf,
}) {
  const rules = fareRules;
  const isVisible = !!showFareRules;
  const showAgentActions = editable && !locked && voucherId && BACKEND_URL;

  const [pasteMode, setPasteMode] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteFormat, setPasteFormat] = useState('bullets');
  const [editingMode, setEditingMode] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [busy, setBusy] = useState(null);

  // Customer / PDF view: collapse entirely when hidden.
  if (!isVisible && !showAgentActions) return null;

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const toggleVisibility = async (next) => {
    try {
      setBusy('toggle');
      await axios.patch(
        `${BACKEND_URL}/api/vouchers/${voucherId}/fare-rules`,
        { showFareRules: next },
        { headers: authHeaders() },
      );
      onRefresh?.();
    } catch (err) {
      window.alert(`Toggle failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(null);
    }
  };

  const autoFetch = async () => {
    try {
      setBusy('auto');
      await axios.post(
        `${BACKEND_URL}/api/vouchers/${voucherId}/fare-rules/auto-fetch`,
        {},
        { headers: authHeaders() },
      );
      onRefresh?.();
    } catch (err) {
      window.alert(`Auto-fetch failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(null);
    }
  };

  const submitPaste = async () => {
    if (!pasteText.trim()) return;
    try {
      setBusy('paste');
      await axios.post(
        `${BACKEND_URL}/api/vouchers/${voucherId}/fare-rules/from-text`,
        { text: pasteText, format: pasteFormat },
        { headers: authHeaders() },
      );
      setPasteMode(false);
      setPasteText('');
      onRefresh?.();
    } catch (err) {
      window.alert(`Synthesis failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(null);
    }
  };

  const beginEdit = () => {
    if (Array.isArray(rules)) {
      setDraftText(rules.join('\n'));
    } else if (typeof rules === 'string') {
      setDraftText(rules);
    } else {
      setDraftText('');
    }
    setEditingMode(true);
  };

  const saveEdit = async () => {
    // If draft has multiple lines, save as array (bullets). Single line → string.
    const lines = draftText
      .split('\n')
      .map((s) => s.replace(/^[\s\-•·\*]+/, '').trim())
      .filter(Boolean);
    const value = lines.length > 1 ? lines : (lines[0] || '');
    try {
      setBusy('save');
      await axios.patch(
        `${BACKEND_URL}/api/vouchers/${voucherId}/fare-rules`,
        { fareRules: value, showFareRules: true },
        { headers: authHeaders() },
      );
      setEditingMode(false);
      onRefresh?.();
    } catch (err) {
      window.alert(`Save failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(null);
    }
  };

  const renderRulesBody = () => {
    if (Array.isArray(rules) && rules.length > 0) {
      return (
        <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }} data-testid="fare-rules-bullets">
          {rules.map((r, i) => (
            <li
              key={i}
              style={{
                fontSize: '12px',
                color: '#1F2937',
                lineHeight: 1.55,
                marginBottom: '4px',
                paddingLeft: '14px',
                textIndent: '-14px',
              }}
            >
              <span style={{ color: accent, fontWeight: 700, marginRight: '6px' }}>•</span>
              {r}
            </li>
          ))}
        </ul>
      );
    }
    if (typeof rules === 'string' && rules.trim()) {
      return (
        <p
          data-testid="fare-rules-paragraph"
          style={{ fontSize: '12px', color: '#1F2937', lineHeight: 1.55, margin: 0 }}
        >
          {rules}
        </p>
      );
    }
    return (
      <div
        style={{ fontSize: '11px', color: '#6B7280', fontStyle: 'italic' }}
        data-testid="fare-rules-empty"
      >
        No fare rules captured yet.{showAgentActions && ' Click Auto-Fetch or Paste-Text to populate.'}
      </div>
    );
  };

  return (
    <SectionCard
      title="Fare Rules"
      icon={FileText}
      accent={accent}
      testId="fare-rules-card"
      className="mb-4"
      headerAction={showAgentActions ? (
        <label
          className="agent-toolbar"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '11px',
            color: accent,
            fontWeight: 600,
            letterSpacing: '0.04em',
            cursor: busy === 'toggle' ? 'wait' : 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={isVisible}
            disabled={busy === 'toggle'}
            onChange={(e) => toggleVisibility(e.target.checked)}
            data-testid="fare-rules-visibility-toggle"
            style={{ cursor: 'inherit' }}
          />
          <span>Show on voucher</span>
        </label>
      ) : null}
    >
      {/* Agent-only action row */}
      {showAgentActions && !pasteMode && !editingMode && (
        <div
          className="agent-toolbar"
          style={{ marginBottom: rules ? '10px' : 0, display: 'flex', flexWrap: 'wrap', gap: '6px' }}
        >
          <button
            type="button"
            data-testid="fare-rules-auto-fetch-btn"
            onClick={autoFetch}
            disabled={busy !== null || !hasSourcePdf}
            title={!hasSourcePdf ? 'Original PDF not retained — use Paste-Text instead' : 'Re-run LLM extraction on the original PDF'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '6px 10px', border: '1px solid #E5E7EB',
              background: hasSourcePdf ? '#FFFFFF' : '#F9FAFB',
              color: hasSourcePdf ? '#374151' : '#9CA3AF',
              borderRadius: '6px', fontSize: '11px', fontWeight: 600,
              cursor: busy === 'auto' ? 'wait' : (hasSourcePdf ? 'pointer' : 'not-allowed'),
              letterSpacing: '0.04em',
            }}
          >
            <RefreshCw style={{ width: '12px', height: '12px' }} />
            <span>{busy === 'auto' ? 'Fetching…' : 'Auto-Fetch'}</span>
          </button>
          <button
            type="button"
            data-testid="fare-rules-paste-text-btn"
            onClick={() => setPasteMode(true)}
            disabled={busy !== null}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '6px 10px', border: '1px solid #E5E7EB',
              background: '#FFFFFF', color: '#374151',
              borderRadius: '6px', fontSize: '11px', fontWeight: 600,
              cursor: 'pointer', letterSpacing: '0.04em',
            }}
          >
            <FileText style={{ width: '12px', height: '12px' }} />
            <span>Paste Text</span>
          </button>
          <button
            type="button"
            data-testid="fare-rules-edit-btn"
            onClick={beginEdit}
            disabled={busy !== null}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '6px 10px', border: '1px solid #E5E7EB',
              background: '#FFFFFF', color: '#374151',
              borderRadius: '6px', fontSize: '11px', fontWeight: 600,
              cursor: 'pointer', letterSpacing: '0.04em',
            }}
          >
            <span>Edit manually</span>
          </button>
        </div>
      )}

      {/* Paste-text inline form */}
      {showAgentActions && pasteMode && (
        <div data-testid="fare-rules-paste-form" style={{ marginBottom: '10px' }}>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="Paste raw fare-rules text from GDS / airline portal here…"
            data-testid="fare-rules-paste-textarea"
            rows={6}
            style={{
              width: '100%', fontSize: '12px', padding: '8px',
              border: '1px solid #D1D5DB', borderRadius: '6px',
              fontFamily: 'inherit', resize: 'vertical',
            }}
          />
          <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '11px', color: accent, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <input type="radio" name="fare-fmt" checked={pasteFormat === 'bullets'} onChange={() => setPasteFormat('bullets')} />
              Bullets
            </label>
            <label style={{ fontSize: '11px', color: accent, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <input type="radio" name="fare-fmt" checked={pasteFormat === 'paragraph'} onChange={() => setPasteFormat('paragraph')} />
              Paragraph
            </label>
            <button
              type="button"
              data-testid="fare-rules-paste-submit"
              onClick={submitPaste}
              disabled={busy !== null || !pasteText.trim()}
              style={{
                padding: '6px 12px', border: 'none', background: primary,
                color: '#FFFFFF', borderRadius: '6px', fontSize: '11px',
                fontWeight: 700, cursor: busy === 'paste' ? 'wait' : 'pointer',
                letterSpacing: '0.04em',
              }}
            >
              {busy === 'paste' ? 'Synthesizing…' : 'Synthesize & Save'}
            </button>
            <button
              type="button"
              data-testid="fare-rules-paste-cancel"
              onClick={() => { setPasteMode(false); setPasteText(''); }}
              disabled={busy !== null}
              style={{
                padding: '6px 12px', border: '1px solid #E5E7EB', background: '#FFFFFF',
                color: '#374151', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                cursor: 'pointer', letterSpacing: '0.04em',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Manual-edit inline form */}
      {showAgentActions && editingMode && (
        <div data-testid="fare-rules-edit-form" style={{ marginBottom: '10px' }}>
          <textarea
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            placeholder="One bullet per line, or a single paragraph for paragraph form."
            data-testid="fare-rules-edit-textarea"
            rows={8}
            style={{
              width: '100%', fontSize: '12px', padding: '8px',
              border: '1px solid #D1D5DB', borderRadius: '6px',
              fontFamily: 'inherit', resize: 'vertical',
            }}
          />
          <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
            <button
              type="button"
              data-testid="fare-rules-edit-save"
              onClick={saveEdit}
              disabled={busy !== null}
              style={{
                padding: '6px 12px', border: 'none', background: primary,
                color: '#FFFFFF', borderRadius: '6px', fontSize: '11px',
                fontWeight: 700, cursor: busy === 'save' ? 'wait' : 'pointer',
                letterSpacing: '0.04em',
              }}
            >
              {busy === 'save' ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              data-testid="fare-rules-edit-cancel"
              onClick={() => setEditingMode(false)}
              disabled={busy !== null}
              style={{
                padding: '6px 12px', border: '1px solid #E5E7EB', background: '#FFFFFF',
                color: '#374151', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                cursor: 'pointer', letterSpacing: '0.04em',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Body — only when visible (customer or agent view alike) */}
      {isVisible && !editingMode && !pasteMode && renderRulesBody()}
    </SectionCard>
  );
}


// ---------------------------------------------------------------------------
// FlightVoucherBody (main) — schema-detect + dispatch.
// ---------------------------------------------------------------------------
export default function FlightVoucherBody({
  voucher,
  company,
  editable = false,
  locked = false,
  onFieldSave,
  passengerIndex = null,
  showFareDetails = false,
  // Drives FareDetailsCard's two-mode render. Agent screens (VoucherPreview)
  // pass true → markup line is visible + editable, supplier taxes shown
  // separately. Customer screens (PublicVoucher, public bundle) pass false
  // → markup is invisibly rolled into taxes, no markup row.
  isAgentView = false,
  // Agent action surface — when set, PassengerCard renders the trash icon
  // (delete passenger) and per-sector "Upload barcode" controls; FareRulesCard
  // exposes the auto-fetch / paste-text / inline edit controls. Customer
  // screens never pass these so the public PDF stays clean.
  onRefresh,
}) {
  const f = voucher?.flight || {};
  const primary = company?.primaryColor || '#34D0CB';
  const accent = company?.accentColor || '#595A5B';
  const voucherId = voucher?.voucherId || null;

  const segments = Array.isArray(f.segments) ? f.segments : [];
  const passengersAll = Array.isArray(f.passengers) ? f.passengers : [];
  const isRichSchema = segments.length > 0;
  const allCodes = Array.isArray(voucher?.allCodes) ? voucher.allCodes : [];

  if (!isRichSchema) {
    return (
      <LegacyFlightLayout
        voucher={voucher}
        primary={primary}
        accent={accent}
        editable={editable}
        locked={locked}
        onFieldSave={onFieldSave}
      />
    );
  }

  // Per-passenger PDF — single explicit passenger index.
  const showSinglePax =
    passengerIndex !== null &&
    Number.isInteger(passengerIndex) &&
    passengerIndex >= 0 &&
    passengerIndex < passengersAll.length;

  // Group PDF — cap at first 9 passengers; warn when truncated.
  const passengersToRender = showSinglePax
    ? [passengersAll[passengerIndex]]
    : passengersAll.slice(0, PASSENGERS_PER_PDF_CAP);
  const showCapWarning = !showSinglePax && passengersAll.length > PASSENGERS_PER_PDF_CAP;

  const firstSeg = segments[0] || {};
  const lastSeg = segments[segments.length - 1] || firstSeg;
  const heroFromCode = firstSeg.departureAirportCode || firstSeg.departureCity || '';
  const heroFromCity = firstSeg.departureCity || '';
  const heroToCode = lastSeg.arrivalAirportCode || lastSeg.arrivalCity || '';
  const heroToCity = lastSeg.arrivalCity || '';

  // Render-time trip type + concise header route. We intentionally do NOT
  // trust the LLM-extracted `flight.tripType` because it's been observed to
  // mislabel one-way-with-connection as multi-city.
  const computedTripType = deriveTripType(segments);
  const headerRoute = buildHeaderRoute(segments);

  const headerRefs = [
    f.bookingReference && { label: 'Booking Ref', value: f.bookingReference },
    f.pnr && { label: 'Airline PNR', value: f.pnr },
    // f.eTicketNumber removed — it's now per-passenger only.
    f.issuingAirline && { label: 'Issued by', value: f.issuingAirline },
    f.issueDate && { label: 'Date of Issue', value: formatVoucherDate(f.issueDate) },
  ].filter(Boolean);

  return (
    <div data-testid="flight-rich-layout">
      <div style={{ display: 'table', width: '100%', marginBottom: '14px' }}>
        <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
          {/* New header: "E-Ticket · PNQ – CCU via DEL" (concise route). Falls
              back to the legacy "Flight Itinerary" string when we don't have
              enough airport codes to build a route. */}
          <SectionTitle primary={primary} data-testid="flight-header-route">
            {headerRoute ? `E-Ticket · ${headerRoute}` : 'Flight Itinerary'}
          </SectionTitle>
        </div>
        <div style={{ display: 'table-cell', verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap' }}>
          {/* Computed trip-type pill — ignores the extracted field. */}
          <TripTypePill tripType={computedTripType} primary={primary} accent={accent} />
        </div>
      </div>

      {showSinglePax && (
        <div
          data-testid="flight-single-pax-banner"
          style={{
            padding: '8px 12px',
            background: `${primary}14`,
            border: `1px solid ${primary}33`,
            borderRadius: '6px',
            marginBottom: '14px',
            fontSize: '12px',
            color: accent,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          Voucher for {passengersAll[passengerIndex]?.fullName || `Passenger ${passengerIndex + 1}`}
        </div>
      )}

      {headerRefs.length > 0 && (
        <div style={{ marginBottom: '16px' }} data-testid="flight-header-refs">
          {headerRefs.map((r) => (
            <span
              key={r.label}
              style={{
                display: 'inline-block',
                padding: '4px 10px',
                marginRight: '8px',
                marginBottom: '6px',
                background: '#F8FAFC',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                fontSize: '11px',
                color: '#334155',
              }}
            >
              <span style={{ fontWeight: 600, color: accent, letterSpacing: '0.08em', textTransform: 'uppercase', marginRight: '6px' }}>
                {r.label}
              </span>
              <span className="font-mono">{r.value}</span>
            </span>
          ))}
        </div>
      )}

      <div style={{
        display: 'table', width: '100%',
        padding: '20px', borderRadius: '12px', marginBottom: '20px',
        background: `${primary}14`, border: `1px solid ${primary}33`,
      }}>
        <div style={{ display: 'table-cell', verticalAlign: 'middle', width: '45%' }}>
          <div className="text-[9px] font-semibold uppercase" style={{ letterSpacing: '0.2em', color: accent }}>From</div>
          <div className="text-4xl font-extrabold tracking-tight mt-1" style={{ color: '#111827' }}>
            {heroFromCode || '—'}
          </div>
          <div className="text-xs mt-1" style={{ color: accent }}>{heroFromCity}</div>
        </div>
        <div style={{ display: 'table-cell', verticalAlign: 'middle', textAlign: 'center', width: '10%' }}>
          <FlightIcon width={36} height={36} color={primary} />
        </div>
        <div style={{ display: 'table-cell', verticalAlign: 'middle', width: '45%', textAlign: 'right' }}>
          <div className="text-[9px] font-semibold uppercase" style={{ letterSpacing: '0.2em', color: accent }}>To</div>
          <div className="text-4xl font-extrabold tracking-tight mt-1" style={{ color: '#111827' }}>
            {heroToCode || '—'}
          </div>
          <div className="text-xs mt-1" style={{ color: accent }}>{heroToCity}</div>
        </div>
      </div>

      <div data-testid="flight-segments-list">
        {segments.map((seg, idx) => {
          // Compute layover info to NEXT segment (skip on the last leg).
          let layoverLabel = null;
          let aircraftChange = false;
          if (idx < segments.length - 1) {
            const next = segments[idx + 1];
            const arr = parseSegmentInstant(seg.arrivalDate, seg.arrivalTime);
            const dep = parseSegmentInstant(next?.departureDate, next?.departureTime);
            if (arr && dep) {
              const mins = Math.round((dep - arr) / 60000);
              layoverLabel = fmtLayoverLabel(mins, seg.arrivalCity, seg.arrivalAirportCode);
            }
            // aircraftChange flag lives on the SECOND leg of the pair (the
            // one passengers re-board) per IATA convention. Falls back to
            // the first leg if the airline put it there instead.
            aircraftChange = !!(next?.aircraftChange ?? seg.aircraftChange);
          }
          return (
            <React.Fragment key={idx}>
              <SegmentCard
                seg={seg}
                idx={idx}
                primary={primary}
                accent={accent}
                isLast={idx === segments.length - 1}
              />
              {(layoverLabel || aircraftChange) && (
                <LayoverPill
                  idx={idx}
                  label={layoverLabel || 'Connection'}
                  aircraftChange={aircraftChange}
                  accent={accent}
                  primary={primary}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <SectionTitle primary={primary}>
        Passenger Details{passengersAll.length > 1 && !showSinglePax ? ` (${Math.min(passengersAll.length, PASSENGERS_PER_PDF_CAP)} of ${passengersAll.length})` : ''}
      </SectionTitle>
      {showCapWarning && <PassengerCapWarning total={passengersAll.length} accent={accent} />}
      <div data-testid="flight-passengers-list">
        {passengersToRender.map((pax, i) => (
          <PassengerCard
            key={i}
            passenger={pax}
            pIdx={showSinglePax ? passengerIndex : i}
            segments={segments}
            allCodes={allCodes}
            primary={primary}
            accent={accent}
            editable={editable}
            locked={locked}
            onFieldSave={onFieldSave}
            voucherId={voucherId}
            onRefresh={onRefresh}
            totalPassengers={passengersAll.length}
          />
        ))}
      </div>

      {showFareDetails && (
        <FareDetailsCard
          fare={f.fareDetails}
          markup={{
            amount: f.markup_amount,
            type: f.markup_type,
            note: f.markup_note,
          }}
          accent={accent}
          isAgentView={isAgentView}
          editable={editable}
          locked={locked}
          onFieldSave={onFieldSave}
        />
      )}

      <FareRulesCard
        fareRules={f.fareRules}
        showFareRules={f.showFareRules}
        accent={accent}
        primary={primary}
        editable={editable}
        locked={locked}
        voucherId={voucherId}
        onRefresh={onRefresh}
        hasSourcePdf={!!voucher?.sourcePdfPath}
      />

      {/* PoliciesCard is ALWAYS customer-visible — it carries fare rules,
          cancellation policy, baggage rules etc that the traveller needs.
          The component self-hides when no policy field is populated, so no
          conditional gate needed here. */}
      <PoliciesCard policies={f.policies} accent={accent} />
    </div>
  );
}
